import { DateTime } from "luxon";
import pLimit from "p-limit";
import { useRef } from "react";
import { v4 as randomUuid } from "uuid";
import { Instructor } from "../../providers/instructorsProvider";
import { serverAPI } from "../../api/server.api";
import { OtherEventForm, type OtherEventFormValues } from "../../misc/calendar/OtherEventForm";
import { useOtherEventCategories } from "../../hooks/useOtherEventCategories";
import { createOtherEventDtos } from "./createOtherEventDtos";

const CREATE_OTHER_EVENT_CONCURRENCY = 5;

export function CreateOtherEventForm(props: {
  instructor: Instructor;
  initialDateTime?: DateTime;
  onSuccess: () => void;
}) {
  const { instructor, initialDateTime, onSuccess } = props;
  const otherEventCategories = useOtherEventCategories();
  const pendingDtosRef = useRef<ReturnType<typeof createOtherEventDtos>>();

  const onSubmit = async (formValues: OtherEventFormValues) =>
    runSafely(
      async () => {
        if (!pendingDtosRef.current) {
          const { categoryId } = formValues;
          if (!categoryId) {
            throw new Error(`Invalid categoryId: ${categoryId}`);
          }
          const submittedCategory = otherEventCategories.find((c) => c.id === categoryId);
          if (!submittedCategory) {
            throw new Error(`Category with id ${categoryId} not found.`);
          }
          pendingDtosRef.current = createOtherEventDtos({
            category: submittedCategory,
            createUid: randomUuid,
            formValues,
            instructorId: instructor.id,
          });
        }

        const dtos = pendingDtosRef.current;
        const createOtherEventLimit = pLimit(CREATE_OTHER_EVENT_CONCURRENCY);
        const results = await Promise.allSettled(
          dtos.map((dto) => createOtherEventLimit(() => serverAPI.createCalendarEvent(dto))),
        );
        pendingDtosRef.current = dtos.filter((_, index) => results[index].status === "rejected");

        const failedResult = results.find((result) => result.status === "rejected");
        if (failedResult) {
          throw failedResult.reason;
        }

        pendingDtosRef.current = undefined;
      },
      {
        errorMessage: "Fehler beim Anlegen des Termins.",
        successMessage: "Termin erfolgreich angelegt.",
        onSuccess,
      },
    );

  return <OtherEventForm initialDateTime={initialDateTime} onSubmit={onSubmit} />;
}
